import { AspectRatio, MediaQuery, generateImageRenditions, type ResponsiveImageSpec } from '../../utils/ImageUtils';
import type { AImageProps } from '../../atoms/AImage/AImage.types';
import { MMediaBorderAspectRatio } from '../../molecules/MMediaBorder/MMediaBorder.types';
import type { MMediaStackProps } from '../../molecules/MMediaStack/MMediaStack.types';
import { MMediaStackAspectRatio, MMediaStackImageDirection } from '../../molecules/MMediaStack/MMediaStack.types';

/** True when AEM provides a usable image URL (non-empty, not a null placeholder). */
export const isValidAssetPath = (assetPath?: string | null): assetPath is string => {
  if (typeof assetPath !== 'string') {
    return false;
  }

  const trimmed = assetPath.trim();
  if (!trimmed) {
    return false;
  }

  // AEM may send malformed placeholder URLs such as "http://localhost:4502null".
  return !trimmed.endsWith('null');
};

export const hasValidAImage = (aImage?: AImageProps): boolean =>
  isValidAssetPath(aImage?.image?.assetPath ?? aImage?.src);

export const getMMediaStackSecondaryAImage = (stack?: MMediaStackProps): AImageProps | undefined =>
  stack?.mMediaBorder?.mMediaSelector?.mMediaImage?.aImage;

export const hasAuthoredMMediaStackImages = (stack?: MMediaStackProps): boolean =>
  Boolean(stack && (hasValidAImage(stack.aImage) || hasValidAImage(getMMediaStackSecondaryAImage(stack))));

/** Figma composite aspect ratio: primary 1:1 square + secondary 2:3 overlay. */
export const isOneOnePlusTwoThreeAspectRatio = (value?: MMediaStackAspectRatio | string | null): boolean =>
  value === MMediaStackAspectRatio.OneOnePlusTwoThree || value === '1:1 + 2:3';

/** Normalize AEM / JSON string values to MMediaStack aspectRatio enum. */
export const resolveMMediaStackAspectRatio = (
  value?: MMediaStackAspectRatio | string | null
): MMediaStackAspectRatio => {
  if (isOneOnePlusTwoThreeAspectRatio(value)) {
    return MMediaStackAspectRatio.OneOnePlusTwoThree;
  }
  if (value === MMediaStackAspectRatio.OneOne || value === '1:1') {
    return MMediaStackAspectRatio.OneOne;
  }
  return MMediaStackAspectRatio.TwoThree;
};

/** Normalize AEM / JSON imageDirection; 1:1 + 2:3 always uses Centered stack per Figma. */
export const resolveMMediaStackImageDirection = (
  value?: MMediaStackImageDirection | string | null,
  aspectRatio?: MMediaStackAspectRatio | string | null
): MMediaStackImageDirection => {
  if (isOneOnePlusTwoThreeAspectRatio(aspectRatio)) {
    return MMediaStackImageDirection.Centered;
  }
  if (value === MMediaStackImageDirection.Centered || value === 'Centered') {
    return MMediaStackImageDirection.Centered;
  }
  return MMediaStackImageDirection.Diagonal;
};

/** Convert single-ratio mMediaStack.aspectRatio to valid CSS (not used for 1:1 + 2:3 composite). */
export const getAspectRatioCssValue = (aspectRatio: MMediaStackAspectRatio): string => {
  if (aspectRatio === MMediaStackAspectRatio.OneOne) {
    return '1 / 1';
  }
  return '2 / 3';
};

const buildRenditionSpecs = (imageAspectRatio: AspectRatio): ResponsiveImageSpec[] => [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: imageAspectRatio, imgWidthPx: 540 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: imageAspectRatio, imgWidthPx: 360 },
  { media: MediaQuery.MIN_WIDTH_LG, aspectRatio: imageAspectRatio, imgWidthPx: 585 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: imageAspectRatio, imgWidthPx: 750 },
];

/** Secondary image renditions for editorial highlight (overrides MMediaImage 3/2 at desktop). */
export const getEditorialHighlightMediaImageRenditionSpecs = (
  stackAspectRatio: MMediaStackAspectRatio
): ResponsiveImageSpec[] => {
  const imageAspectRatio =
    stackAspectRatio === MMediaStackAspectRatio.OneOne ? AspectRatio.SQUARE : AspectRatio.CLASSIC_VER;
  return buildRenditionSpecs(imageAspectRatio);
};

/** Stack props after editorial-highlight normalization (aspectRatio / imageDirection always set). */
export type EnrichedMMediaStackProps = MMediaStackProps & {
  aspectRatio: MMediaStackAspectRatio;
  imageDirection: MMediaStackImageDirection;
};

/** Apply mMediaStack.aspectRatio from AEM / kitchen-sink JSON. */
export const enrichMMediaStackForEditorialHighlight = (stack: MMediaStackProps): EnrichedMMediaStackProps => {
  const aspectRatio = resolveMMediaStackAspectRatio(stack.aspectRatio);
  const imageDirection = resolveMMediaStackImageDirection(stack.imageDirection, aspectRatio);
  const isComposite = aspectRatio === MMediaStackAspectRatio.OneOnePlusTwoThree;
  const mMediaBorderAspectRatio = isComposite
    ? MMediaBorderAspectRatio.TwoThree
    : aspectRatio === MMediaStackAspectRatio.OneOne
    ? MMediaBorderAspectRatio.OneOne
    : stack.mMediaBorder?.aspectRatio ?? MMediaBorderAspectRatio.TwoThree;

  const mMediaImage = stack.mMediaBorder?.mMediaSelector?.mMediaImage;
  const secondaryRenditionSpecs = isComposite
    ? buildRenditionSpecs(AspectRatio.CLASSIC_VER)
    : getEditorialHighlightMediaImageRenditionSpecs(aspectRatio);
  const primaryRenditionSpecs = isComposite
    ? buildRenditionSpecs(AspectRatio.SQUARE)
    : buildRenditionSpecs(aspectRatio === MMediaStackAspectRatio.OneOne ? AspectRatio.SQUARE : AspectRatio.CLASSIC_VER);

  const primaryImage = stack.aImage?.image;
  const enrichedPrimaryAImage =
    primaryImage &&
    isValidAssetPath(primaryImage.assetPath) &&
    primaryRenditionSpecs &&
    !stack.aImage?.renditions?.length
      ? {
          ...stack.aImage,
          renditions: generateImageRenditions(primaryRenditionSpecs, primaryImage.assetPath),
        }
      : stack.aImage;

  return {
    ...stack,
    aspectRatio,
    imageDirection,
    aImage: enrichedPrimaryAImage,
    mMediaBorder: stack.mMediaBorder
      ? {
          ...stack.mMediaBorder,
          aspectRatio: mMediaBorderAspectRatio,
          mMediaSelector: stack.mMediaBorder.mMediaSelector
            ? {
                ...stack.mMediaBorder.mMediaSelector,
                mMediaImage: mMediaImage
                  ? {
                      ...mMediaImage,
                      renditionSpecs: mMediaImage.renditionSpecs ?? secondaryRenditionSpecs,
                    }
                  : mMediaImage,
              }
            : stack.mMediaBorder.mMediaSelector,
        }
      : stack.mMediaBorder,
  };
};

/** Normalize stack props and omit unauthored images before rendering in editorial highlight. */
export const sanitizeMMediaStackForEditorialHighlight = (
  stack: MMediaStackProps
): EnrichedMMediaStackProps | undefined => {
  const enriched = enrichMMediaStackForEditorialHighlight(stack);
  const hasPrimary = hasValidAImage(enriched.aImage);
  const hasSecondary = hasValidAImage(getMMediaStackSecondaryAImage(enriched));

  // Primary image is required; MMediaStack always renders a primary slot.
  if (!hasPrimary) {
    return undefined;
  }

  return {
    ...enriched,
    mMediaBorder: hasSecondary ? enriched.mMediaBorder : undefined,
  };
};
